import { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useBodyScrollLock from '../../../hooks/useBodyScrollLock';

// Terms content component - shared between desktop and mobile checkout
export function TermsContent() {
  return (
    <div className="space-y-8 text-sm text-gray-700 dark:text-gray-300">
      {/* ============================================ */}
      {/* TERMS OF USE */}
      {/* ============================================ */}

      {/* Section 1 - Introduction */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Terms of Use</h2>
        <p className="mb-3">Thank you for using A ONE Institute.</p>
        <p className="mb-3">
          A ONE INSTITUTE offers products and services provided by A ONE Institute, Inc., our subsidiaries, and our international branches, including A ONE Institute Asia B.V., a limited liability company incorporated in the United States (EIN number 81-4942498) (collectively, &quot;A ONE Institute,&quot; &quot;us,&quot; &quot;we,&quot; or &quot;our&quot;). These Terms of Use (&quot;Terms&quot;) govern your use of our website, apps, and other products and services (&quot;Services&quot;). As some of our Services may be software that is downloaded to your computer, phone, tablet, or other device, you agree that we may automatically update this software, and that these Terms will apply to such updates. Please read these Terms carefully, and contact us if you have any questions, requests for information, or complaints. By clicking &quot;I accept&quot; (or for those who have not yet created an account, by using our Services), you agree to be bound by these Terms, including the policies referenced in these Terms.
        </p>
        <p className="mb-3 font-medium text-xs">
          THESE TERMS INCLUDE AN ARBITRATION AGREEMENT AND CLASS ACTION WAIVER THAT APPLY TO ALL CLAIMS BROUGHT AGAINST A ONE INSTITUTE. PLEASE READ THEM CAREFULLY; THEY AFFECT YOUR LEGAL RIGHTS.
        </p>
      </section>

      {/* Section 2 - Using Services */}
      <section>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">1. Using A ONE INSTITUTE&apos;s Web Pages</h3>
        <p className="font-medium text-gray-800 dark:text-gray-200 mb-2">Who May Use Our Services</p>
        <p className="mb-2">Any use or access by anyone under the age of 13 is strictly prohibited.</p>
        <p className="mb-2">You may use our Services only if:</p>
        <ul className="list-disc ml-5 space-y-1 mb-3">
          <li>You can form a binding contract with A ONE INSTITUTE;</li>
          <li>You comply with these Terms, all applicable laws, and our policies;</li>
          <li>You are over the age at which you can provide consent to data processing under the laws of your country.</li>
        </ul>
        <p className="mb-3">
          Any violation of our Terms, applicable laws, or Policies may result in your access to all or part of the Services being suspended, disabled, or terminated.
        </p>
        <p className="font-medium text-gray-800 dark:text-gray-200 mb-2">Our License to You</p>
        <p className="mb-3">
          Subject to these Terms and our Policies, we grant you a limited, personal, non-exclusive, non-transferable, and revocable license right to use our Services. The rights granted herein are only for your personal, non-commercial use.
        </p>
      </section>

      {/* Section 3 - Content Offerings */}
      <section>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">2. Content Offerings</h3>
        <p className="font-medium text-gray-800 dark:text-gray-200 mb-2">No Academic Credit</p>
        <p className="mb-3">
          A ONE INSTITUTE does not grant academic credit for the completion of Content Offerings. Unless otherwise explicitly indicated by a credit-granting institution, participation in or completion of Content Offerings does not confer any academic credit.
        </p>
      </section>

      {/* Section 4 - Security */}
      <section>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">3. Security</h3>
        <p className="mb-3">
          We care about the security of our users. While we work to protect the security of your account and related information, A ONE INSTITUTE cannot guarantee that unauthorized third parties will not be able to defeat our security measures. Please notify us immediately of any compromise or unauthorized use of your account by emailing aoneinstitutesat@gmail.com.
        </p>
      </section>

      {/* Section 5 - Third Party Content */}
      <section>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">4. Third Party Content</h3>
        <p className="mb-3">
          Through the Services, you will have the ability to access and/or use content provided by instructors, other users, and/or other third parties and links to websites and services maintained by third parties. A ONE INSTITUTE disclaims any responsibility or liability related to your access or use of, or inability to access or use, such third party content.
        </p>
      </section>

      {/* Section 6 - Paid Services */}
      <section>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">5. Paid Services from A ONE INSTITUTE</h3>
        <p className="mb-3">
          A ONE INSTITUTE offers paid Services (e.g., online test or online lecture) for a fee. Unless otherwise stated, fees are quoted in United States currency ($). You are responsible for paying all fees charged by or for A ONE INSTITUTE and applicable taxes in a timely manner. Refunds may be available for paid Services as described in our Refund Policy below.
        </p>
      </section>

      {/* Section 7 - Disclaimers */}
      <section>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">6. Disclaimers</h3>
        <p className="mb-3 text-xs">
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE SERVICES AND ALL INCLUDED CONTENT ARE PROVIDED ON AN &quot;AS IS&quot; BASIS WITHOUT WARRANTY OF ANY KIND, WHETHER EXPRESS OR IMPLIED. THE A ONE INSTITUTE PARTIES SPECIFICALLY DISCLAIM ANY AND ALL WARRANTIES AND CONDITIONS OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
        </p>
      </section>

      {/* Section 8 - Limitation of Liability */}
      <section>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">7. Limitation of Liability</h3>
        <p className="mb-3 text-xs">
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE A ONE INSTITUTE PARTIES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES. IN NO EVENT SHALL A ONE INSTITUTE&apos;S AGGREGATE LIABILITY FOR ALL CLAIMS RELATED TO THE SERVICES EXCEED TWENTY U.S. DOLLARS ($20) OR THE TOTAL AMOUNT OF FEES RECEIVED BY A ONE INSTITUTE FROM YOU FOR THE USE OF PAID SERVICES DURING THE PAST SIX MONTHS, WHICHEVER IS GREATER.
        </p>
      </section>

      {/* Section 9 - Governing Law */}
      <section>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">8. Governing Law and Venue</h3>
        <p className="mb-3">
          The Services are managed by A ONE INSTITUTE, Inc. which is located in Bergen County, New Jersey. You agree that these Terms will be governed by the laws of the State of New Jersey, excluding its conflicts of law provisions.
        </p>
      </section>

      {/* Section 10 - Arbitration */}
      <section>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">9. Binding Arbitration and Class Action Waiver</h3>
        <p className="mb-3">
          You and A ONE INSTITUTE agree to submit to binding arbitration any and all disputes, claims, or controversies of any kind that arise out of or relate to our Services, these Terms, and/or any other relationship or dispute between you and us. Neither you nor A ONE INSTITUTE may act as a class representative, nor participate as a member of a class of claimants, with respect to any Claim.
        </p>
      </section>

      {/* Acceptable Use Policy */}
      <section>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Acceptable Use Policy</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Effective: January 1, 2023</p>
        <p className="mb-2">You are prohibited from using our Services to share content that:</p>
        <ul className="list-disc ml-5 space-y-1 mb-3">
          <li>Contains illegal content or promotes illegal activities</li>
          <li>Contains credible threats or organizes acts of real-world violence</li>
          <li>Harasses others</li>
          <li>Violates intellectual property, privacy, or other rights</li>
          <li>Spams others</li>
        </ul>
      </section>

      {/* Copyright and Trademark Policy */}
      <section>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Copyright and Trademark Policy</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Effective: January 1, 2023</p>
        <p className="mb-3">
          A ONE INSTITUTE respects the intellectual property rights of our Content Providers, instructors, users, and other third parties and expects our users to do the same. We reserve the right to suspend, disable, or terminate the accounts of users who repeatedly infringe or are repeatedly charged with infringing the copyrights, trademarks, or other intellectual property rights of others.
        </p>
      </section>

      {/* Course Enrollment Payment Terms */}
      <section>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Course Enrollment Payment Terms</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Effective as of January 1, 2025</p>
        <p className="mb-3">
          The following terms apply to course enrollment payments made through the A ONE Institute Parent Portal.
        </p>

        <h4 className="font-medium text-gray-800 dark:text-gray-200 mt-4 mb-2">Payment Processing</h4>
        <p className="mb-3">
          All course enrollment payments are processed securely through Stripe, a third-party payment processor.
          By proceeding with payment, you authorize A ONE Institute to charge the payment method you provide
          for the total amount displayed at checkout, which includes tuition fees, textbook fees, and exam fees as applicable.
        </p>

        <h4 className="font-medium text-gray-800 dark:text-gray-200 mt-4 mb-2">Payment Confirmation</h4>
        <p className="mb-3">
          Upon successful payment, your student&apos;s course enrollment will be automatically confirmed.
          You will receive a confirmation on-screen and the course status will be updated in your Parent Portal.
          Please retain your payment confirmation for your records.
        </p>
      </section>

      {/* Refund Policy */}
      <section>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Refund Policy</h3>

        <h4 className="font-medium text-gray-800 dark:text-gray-200 mt-4 mb-2">Course Enrollment Refunds</h4>
        <ul className="list-disc ml-5 space-y-2 mb-3">
          <li>
            <strong className="dark:text-white">Before Course Start Date:</strong> Full refund available if requested at least 7 days
            before the course start date. Requests made within 7 days of the course start date may be
            subject to a 15% administrative fee.
          </li>
          <li>
            <strong className="dark:text-white">After Course Start Date:</strong> No refunds will be issued once the course has begun,
            except in cases of documented medical emergencies or family emergencies, which will be reviewed
            on a case-by-case basis.
          </li>
          <li>
            <strong className="dark:text-white">Textbook Fees:</strong> Textbook fees are non-refundable once textbooks have been
            ordered or distributed to the student.
          </li>
          <li>
            <strong className="dark:text-white">Exam Fees:</strong> Exam fees are non-refundable once the exam registration has been
            submitted to the testing organization.
          </li>
        </ul>

        <h4 className="font-medium text-gray-800 dark:text-gray-200 mt-4 mb-2">Requesting a Refund</h4>
        <p className="mb-3">
          To request a refund, please contact A ONE Institute at aoneinstitutesat@gmail.com or call our
          office during business hours. Include your student&apos;s name, the course name, payment date, and
          reason for the refund request. Refund requests are typically processed within 5-10 business days.
        </p>

        <h4 className="font-medium text-gray-800 dark:text-gray-200 mt-4 mb-2">Failed Payments</h4>
        <p className="mb-3">
          If your payment fails, your course enrollment will not be confirmed. You may attempt payment
          again with a different payment method. A ONE Institute is not responsible for any fees charged
          by your bank or payment provider for failed transactions.
        </p>

        <h4 className="font-medium text-gray-800 dark:text-gray-200 mt-4 mb-2">Disputes and Chargebacks</h4>
        <p className="mb-3">
          If you believe there has been an error with your payment, please contact us directly before
          initiating a dispute with your bank or credit card company. Unauthorized chargebacks may result
          in the suspension of your student&apos;s enrollment and access to A ONE Institute services.
        </p>
      </section>

      {/* ============================================ */}
      {/* PRIVACY POLICY */}
      {/* ============================================ */}

      <section className="pt-6 border-t border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Privacy Policy</h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Effective as of January 1, 2024</p>

        <p className="font-medium text-gray-800 dark:text-gray-200 mb-2">Key Information</p>
        <ul className="list-disc ml-5 space-y-2 mb-4">
          <li>A ONE INSTITUTE, Inc. is the data controller of the personal information we collect about you.</li>
          <li>We collect personal information including account registration details such as name and email, details of Content Offerings you undertake, survey information, identity verification data, and information about your use of our site and Services.</li>
          <li>We use your personal information for providing our site and Services, ensuring security and performance, conducting research, sharing information with Content Providers and suppliers, direct marketing, and performing statistical analysis.</li>
          <li>You have rights that you can exercise in relation to our use of your personal information.</li>
        </ul>
      </section>

      {/* How We Use Information */}
      <section>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">How We Use the Information</h3>
        <p className="mb-2">
          We use information relating to your use of the Site to build higher quality, more useful Services by performing statistical analyses of the collective characteristics and behavior of our users, and by measuring demographics and interests regarding specific areas of our Site.
        </p>
        <p className="mb-2">
          We use Personal Data which you provide to us in order to allow you to access and use the Site and in order to provide any information, products, or Services that you request from us.
        </p>
        <p className="mb-3">
          We may use Personal Data to provide technical support to you, where required, and to ensure the security of our Services and the Site.
        </p>
      </section>

      {/* What Information We Collect */}
      <section>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">What Information We Collect</h3>
        <p className="mb-2">We gather the following types of information about users through the Site:</p>
        <ul className="list-disc ml-5 space-y-2 mb-3">
          <li><strong className="dark:text-white">Information relating to your use of our Site:</strong> We may track, collect, and aggregate information including which pages were visited, the order in which they were visited, IP address, operating system, and browser software.</li>
          <li><strong className="dark:text-white">Personal Data provided directly by you:</strong> We collect Personal Data that you provide when you register for an account, update information, purchase products or Services, complete surveys, sign up for email updates, or participate in Content Offerings.</li>
          <li><strong className="dark:text-white">Account Registration:</strong> If you register for an account, you may be required to provide Personal Data such as your name and email address.</li>
          <li><strong className="dark:text-white">Third Party Payment Processing:</strong> A ONE INSTITUTE provides you with the ability to pay using a credit card or other methods through a third party payment processing service provider. Please note that our service provider – not A ONE INSTITUTE – collects and processes your financial information.</li>
        </ul>
      </section>

      {/* Disclosure of Information */}
      <section>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Disclosure of Information</h3>
        <p className="mb-2">
          Except as set forth in this Privacy Notice or as specifically agreed to by you, A ONE INSTITUTE will not disclose any of your Personal Data.
        </p>
        <ul className="list-disc ml-5 space-y-2 mb-3">
          <li><strong className="dark:text-white">Content Providers and Business Partners:</strong> We may share your Personal Data with Content Providers and other business partners so they may share information about their products and services.</li>
          <li><strong className="dark:text-white">Service Providers:</strong> We use various service providers, vendors, and contractors to assist us in providing our Services. Access to your Personal Data by these Contractors is limited to the information reasonably necessary for them to perform their function.</li>
          <li><strong className="dark:text-white">Government Authorities:</strong> A ONE INSTITUTE may share your Personal Data with government authorities in response to subpoenas, court orders, or other legal processes.</li>
          <li><strong className="dark:text-white">Acquirers:</strong> A ONE INSTITUTE may disclose and/or transfer your Personal Data to an acquirer, assignee or other successor entity in connection with a sale, merger, or reorganization.</li>
        </ul>
      </section>

      {/* Legal Basis */}
      <section>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">What You Agree to by Using Our Site</h3>
        <p className="mb-2">We consider that the legal bases for using your personal information are:</p>
        <ul className="list-disc ml-5 space-y-2 mb-3">
          <li>Our use of your personal information is necessary to perform our obligations under any contract with you;</li>
          <li>Our use of your personal information is necessary for complying with our legal obligations;</li>
          <li>Use of your personal information is necessary for our legitimate interests or the legitimate interests of others;</li>
          <li>Consent, to send you certain communications or where required by local law.</li>
        </ul>
        <p className="mb-3">
          If we rely on your consent for us to use your personal information in a particular way, but you later change your mind, you may withdraw your consent by visiting your profile page and we will stop doing so.
        </p>
      </section>

      {/* Contact Information */}
      <section>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Contact Information</h3>
        <p className="mb-2">For questions about these terms or our privacy practices, please contact us:</p>
        <ul className="list-disc ml-5 space-y-1 mb-3">
          <li><strong className="dark:text-white">Email:</strong> aoneinstitutesat@gmail.com</li>
          <li><strong className="dark:text-white">Address:</strong> 65 Challenger Rd Suite 201, Ridgefield Park, NJ 07660</li>
        </ul>
      </section>

      <p className="text-xs text-gray-500 dark:text-gray-400 pt-4 border-t border-gray-200 dark:border-gray-700">
        By checking the agreement box, you acknowledge that you have read, understood, and agree to these
        Terms of Use and Privacy Policy.
      </p>
    </div>
  );
}

function TermsModalContent({ onClose, onDisagree }) {
  const { t } = useTranslation('common');
  const contentRef = useRef(null);
  const [hasReachedEnd, setHasReachedEnd] = useState(false);

  // Check if user has scrolled to the bottom
  const handleScroll = useCallback(() => {
    if (contentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      // Consider "reached end" when within 20px of bottom
      if (scrollTop + clientHeight >= scrollHeight - 20) {
        setHasReachedEnd(true);
      }
    }
  }, []);

  // Callback ref to measure content and auto-enable if it fits without scrolling
  const measureRef = useCallback((node) => {
    contentRef.current = node;
    if (node) {
      const { scrollHeight, clientHeight } = node;
      if (scrollHeight <= clientHeight) {
        setHasReachedEnd(true);
      }
    }
  }, []);

  return (
    <div className="flex items-center justify-center p-4 h-full">
      {/* Backdrop - click to close */}
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onDisagree}
      />

      {/* Modal */}
      <div className="modal-panel relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('checkout.termsPolicy')}</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Important Warning */}
        <div className="px-4 pt-4 pb-2">
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm font-medium text-red-700 dark:text-red-400">
              {t('checkout.termsWarning')}
            </p>
          </div>
        </div>

        {/* Scrollable Content */}
        <div
          ref={measureRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto p-4 overscroll-contain"
          style={{ maxHeight: '50vh' }}
        >
          <TermsContent />
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex gap-3">
          <button
            onClick={onDisagree}
            className="flex-1 py-2.5 px-4 font-medium rounded-lg transition-colors border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            {t('checkout.iDisagree')}
          </button>
          <button
            onClick={onClose}
            disabled={!hasReachedEnd}
            className={`flex-1 py-2.5 px-4 font-medium rounded-lg transition-colors ${
              hasReachedEnd
                ? 'bg-[#589DD5] hover:bg-[#4a8bc4] text-white'
                : 'bg-gray-200 dark:bg-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
            }`}
          >
            {t('checkout.iAgree')}
          </button>
        </div>

        {/* Scroll hint when not at bottom */}
        {!hasReachedEnd && (
          <div className="absolute bottom-20 left-0 right-0 flex justify-center pointer-events-none">
            <span className="text-xs text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 px-2 py-1 rounded-full shadow-sm border border-gray-200 dark:border-gray-600">
              {t('checkout.scrollToAgree')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// Terms Modal component
export default function TermsModal({ isOpen, onClose, onDisagree, zIndex = 200 }) {
  const nodeRef = useRef(null);
  useBodyScrollLock(isOpen);

  return createPortal(
    <CSSTransition
      in={isOpen}
      timeout={200}
      classNames="modal-animate"
      unmountOnExit
      nodeRef={nodeRef}
    >
      <div ref={nodeRef} className="modal-animate" style={{ zIndex }}>
        <TermsModalContent
          onClose={onClose}
          onDisagree={onDisagree}
        />
      </div>
    </CSSTransition>,
    document.body
  );
}
